import { toast } from "react-toastify";
import {
  ApplicantType,
  AnimalScreeningReportVendors,
  VerificationNames
} from "shared/types";
import {
  Question,
  Template,
  VerificationSchemaNames,
  VerificationSchemas,
  VerificationSettings
} from "types/landlord/verificationTemplate";
import { validate } from "uuid";

export const onSaveCleanUp = {
  additionalQuestions: (template: Template) => {
    // Clone template to avoid mutations
    const updatedTemplate = { ...template };

    const cleanQuestions = (questions?: Question[]) => {
      return questions?.reduce<typeof questions>((acc, el) => {
        // Trim all string fields
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- sad but true
        el.name = el.name?.trim() || "";
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- sad but true
        el.title = el.title?.trim() || "";

        // If both name and title are empty, discard the entry
        if (!el.name && !el.title) return acc;

        // Ensure title and name are set correctly
        el.title ||= el.name;
        el.name ||= el.title;

        const startsWithAzD = (candidate: string) =>
          /^[a-zA-Z1-9]/.test(candidate);
        // Check if el.name starts with a letter or DIGIT!

        if (!startsWithAzD(el.name))
          if (startsWithAzD(el.title))
            // if el.title starts with letter, set it to el.name
            el.name = el.title;
          else {
            toast.warn(
              `Invalid question removed: "${el.name}". It must start with a letter.`
            );
            return acc;
          }

        if (RegExp(/^\d+$/).exec(el.name)) {
          toast.warn(
            `Invalid question removed: "${el.name}". It must not contain only numbers.`
          );
          return acc;
        }

        if (el.config?.widget_type === "select") {
          // clear all options are empty
          el.config.options = el.config.options?.filter((option) => option);
          // check if all options are not empty
          if (!el.config.options?.length) {
            toast.warn(
              `Invalid question removed: "${el.name}". It must have at least one option.`
            );
            return acc;
          }
        }

        acc.push(el);
        return acc;
      }, []);
    };

    const { verification_schemas } = updatedTemplate;

    Object.keys(verification_schemas).forEach((tenantType) => {
      const tenant = verification_schemas[tenantType as ApplicantType];

      if (tenant?.[VerificationNames.RentalApplication]?.additional_questions)
        updatedTemplate.verification_schemas[tenantType][
          VerificationNames.RentalApplication
        ].additional_questions = cleanQuestions(
          tenant[VerificationNames.RentalApplication].additional_questions
        );
    });

    return updatedTemplate;
  },
  identityVerificationSettings: (
    template: Template,
    editedName: string
  ): Template => {
    const { settings } = template;

    const applicantTypes = Object.keys(settings) as ApplicantType[];
    // TODO: Check with Max. Previous code didn't consider occupant
    const isSettingsEmpty = applicantTypes.every(
      (type) => !Object.keys(settings[type] || {}).length
    );

    if (!isSettingsEmpty) return { ...template, name: editedName };

    // Default identity verification settings
    const identityVerification = {
      identity_verification: {
        selfie_check: true,
        // TODO: Check with Max. Previous code didn't have this field for occupant
        prevent_apply_if_failed: true
      }
    };

    return {
      ...template,
      // Inject default identity verification settings
      settings: applicantTypes.reduce(
        (acc, type) => ({
          ...acc,
          [type]: { ...settings[type], ...identityVerification }
        }),
        { ...settings } // Initialize with existing settings to preserve structure
      ),
      name: editedName
    };
  },
  onlyOneIncomePresent: (
    template: Template,
    combinedIncomeAvailable?: boolean
  ) => {
    // 1. if both income verifications are available - prefer old income and remove new income
    // 2. If old income enabled - remove new income and vice versa

    const normalizeSchemas = (verifications: VerificationSchemaNames) => {
      const isOldIncomeEnabled = checkIfIncomeTypeIsEnabled(
        VerificationNames.IncomeVerification,
        verifications
      );

      const isNewIncomeEnabled = checkIfIncomeTypeIsEnabled(
        VerificationNames.CombinedIncome,
        verifications
      );

      // Old income is prior to the new income
      if (isOldIncomeEnabled || !combinedIncomeAvailable)
        return {
          ...verifications,
          [VerificationNames.CombinedIncome]: undefined
        };
      else if (isNewIncomeEnabled)
        return {
          ...verifications,
          [VerificationNames.IncomeVerification]: undefined,
          [VerificationNames.VerifiedIncomeVerification]: undefined
        };
      else
        return {
          ...verifications,
          [VerificationNames.IncomeVerification]: undefined,
          [VerificationNames.VerifiedIncomeVerification]: undefined,
          [VerificationNames.CombinedIncome]: undefined
        };
    };

    return {
      ...template,
      verification_schemas: {
        [ApplicantType.Applicant]: normalizeSchemas(
          template.verification_schemas[ApplicantType.Applicant]
        ),
        [ApplicantType.CoSigner]: normalizeSchemas(
          template.verification_schemas[ApplicantType.CoSigner]
        ),
        [ApplicantType.Occupant]: normalizeSchemas(
          template.verification_schemas[ApplicantType.Occupant] || {}
        )
      }
    };
  }
};

export const onMountTemplatePreparations = {
  additionalQuestions: (template: Template) =>
    onSaveCleanUp.additionalQuestions(template),
  // Populate default schemas into verification_schemas FE state. Used to properly display
  // configs when a schema comes as undefined (which is expected but needs special care)
  populateDefaultSchemas: (
    defaultTemplate: Template,
    currentTemplate: Template
  ) => {
    return Object.values(ApplicantType).reduce(
      (acc, applicantType) => {
        const defaultSchemas =
          defaultTemplate.verification_schemas[applicantType] ?? {};
        const currentSchemas = {
          ...(currentTemplate.verification_schemas[applicantType] ??
            defaultSchemas)
        };

        const normalizedSchemas = [
          VerificationNames.RentalApplication,
          VerificationNames.IncomeVerification,
          VerificationNames.VerifiedIncomeVerification,
          VerificationNames.CombinedIncome
        ].reduce((acc, vName) => {
          acc[vName] = onMountTemplatePreparations.normalizeSchema(
            vName,
            currentSchemas,
            defaultSchemas
          );

          return acc;
        }, {});

        return {
          ...acc,
          [applicantType]: {
            ...currentSchemas,
            ...normalizedSchemas
          }
        };
      },
      { applicant: {}, co_signer: {}, occupant: {} }
    ) as VerificationSchemas;
  },
  normalizeSchema: (
    schemaName: VerificationNames,
    currentSchemas: VerificationSchemaNames,
    defaultSchemas: VerificationSchemaNames
  ) => {
    switch (schemaName) {
      case VerificationNames.RentalApplication: {
        const requiredFieldSteps = ["pets", "emergency_contacts", "vehicles"];
        const rentalApplicationRequired = requiredFieldSteps.reduce(
          (acc, step) => {
            const key = `${step}_step_required`;
            return {
              ...acc,
              [key]: {
                ...defaultSchemas.rental_application?.[key],
                ...currentSchemas.rental_application?.[key]
              }
            };
          },
          {}
        );

        return {
          ...currentSchemas.rental_application,
          ...rentalApplicationRequired
        };
      }
      case VerificationNames.IncomeVerification:
        return {
          ...defaultSchemas.income_verification,
          ...currentSchemas.income_verification,
          [VerificationNames.IncomeVerification + "_step_enabled"]:
            checkIfIncomeTypeIsEnabled(
              VerificationNames.IncomeVerification,
              currentSchemas
            )
        };

      case VerificationNames.CombinedIncome:
        return {
          ...defaultSchemas.combined_income_verification,
          ...currentSchemas.combined_income_verification,
          [VerificationNames.CombinedIncome + "_enabled"]:
            checkIfIncomeTypeIsEnabled(
              VerificationNames.CombinedIncome,
              currentSchemas
            )
        };

      case VerificationNames.VerifiedIncomeVerification:
      default:
        return {
          ...defaultSchemas[schemaName],
          ...currentSchemas[schemaName]
        } as VerificationSettings;
    }
  }
};

export const checkIfIncomeTypeIsEnabled = (
  schemaName: VerificationNames,
  verificationSchemas: VerificationSchemaNames
): boolean => {
  switch (schemaName) {
    case VerificationNames.IncomeVerification:
    case VerificationNames.VerifiedIncomeVerification:
      return Boolean(
        verificationSchemas.income_verification?.[
          VerificationNames.IncomeVerification + "_step_enabled"
        ]
      );
    case VerificationNames.CombinedIncome: {
      // Having an empty combined_income_verification object is an old way
      // of saying that this verification is enabled. A rare but possible
      // occasion
      const isEnabledByDeprecatedLogic =
        verificationSchemas.combined_income_verification &&
        !Object.keys(verificationSchemas.combined_income_verification).length &&
        !verificationSchemas[VerificationNames.IncomeVerification]?.[
          VerificationNames.IncomeVerification + "_step_enabled"
        ];
      const isEnabled =
        verificationSchemas[VerificationNames.CombinedIncome]?.[
          VerificationNames.CombinedIncome + "_enabled"
        ];

      return Boolean(isEnabled ?? isEnabledByDeprecatedLogic);
    }

    default:
      return false;
  }
};

export const isValidUUID = (value: string): boolean => {
  return validate(value);
};

export const getPetScreeningSchemaSettings = (
  template: Template,
  partnerType: string,
  setPetScreeningModalOpened: (opened: boolean) => void
) => {
  const existingSchema =
    template.verification_schemas[partnerType]?.[
      VerificationNames.PetScreeningReport
    ];

  const vendor =
    existingSchema?.vendor || AnimalScreeningReportVendors.PetScreening;

  if (
    !template.verification_schemas[partnerType]?.[
      VerificationNames.PetScreeningReport
    ]?.vendor ||
    !template.verification_schemas[partnerType][
      VerificationNames.PetScreeningReport
    ].settings?.screening_url ||
    (vendor === AnimalScreeningReportVendors.OurPetPolicy &&
      !template.verification_schemas[partnerType][
        VerificationNames.PetScreeningReport
      ].settings.property_management_id)
  )
    setPetScreeningModalOpened(true);

  return {
    ...existingSchema,
    settings: {
      ...existingSchema?.settings,
      pet_screening_enabled: true
    },
    vendor
  };
};
